// src/templates/tiptapTemplateBuilders.js
import { parseContentWithMath } from "./helpers/mathFormatter";

// ---------- small helpers ----------
function textNode(text, marks) {
  const node = { type: "text", text };
  if (marks) node.marks = marks;
  return node;
}

function paragraph(content = []) {
  return { type: "paragraph", content };
}

function heading(level, text) {
  return {
    type: "heading",
    attrs: { level },
    content: [textNode(text)],
  };
}

function spacer() {
  return { type: "paragraph" };
}

// If the question is "$0.25$" or "$25%$", it becomes a mathInline node.
// If it’s plain text, it becomes a text node.
// If it contains mixed text + $math$, we parse it.
function inlineFromMaybeMath(str) {
  if (typeof str !== "string") return [textNode("")];

  const trimmed = str.trim();

  // pure inline math like "$0.25$"
  if (/^\$[^$]*\$$/.test(trimmed)) {
    const latex = trimmed.replace(/^\$|\$$/g, "");
    return [{ type: "mathInline", attrs: { latex } }];
  }

  // mixed math content
  if (
    trimmed.includes("$") ||
    trimmed.includes("\\(") ||
    trimmed.includes("\\[")
  ) {
    const parts = parseContentWithMath(trimmed);
    return parts.map((part) => {
      if (part.type === "text") return { type: "text", text: part.text };
      return { type: "mathInline", attrs: part.attrs };
    });
  }

  // plain text
  return [textNode(trimmed)];
}

// ---------- Template builders ----------

// 1) conversion-template
export function buildConversionDoc(conversionQuestions = []) {
  const doc = { type: "doc", content: [] };

  doc.content.push({
    type: "table",
    content: [
      {
        type: "tableRow",
        content: [
          {
            type: "tableCell",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Name:" }],
              },
            ],
          },
          {
            type: "tableCell",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Score:" }],
              },
            ],
          },
        ],
      },
    ],
  });

  doc.content.push({ type: "paragraph" });

  // render in rows of 3
  for (let i = 0; i < conversionQuestions.length; i += 3) {
    const row = conversionQuestions.slice(i, i + 3);
    const rowContent = [];

    row.forEach((q, idx) => {
      const n = i + idx + 1;

      // "1. " bold
      rowContent.push(textNode(`${n}. `, [{ type: "bold" }]));

      // question (inline math usually)
      rowContent.push(...inlineFromMaybeMath(q.question ?? ""));

      rowContent.push(textNode(" = "));
      rowContent.push({ type: "answerBox" });

      if (idx < row.length - 1) rowContent.push(textNode("        "));
    });

    doc.content.push(paragraph(rowContent));
    doc.content.push(spacer());
  }

  // // Answer key (optional)
  doc.content.push(heading(2, "Answer Key"));
  // doc.content.push(spacer());

  const total = conversionQuestions.length;
  const half = Math.ceil(total / 2);

  // Build left column paragraphs (1–15)
  const leftColumnContent = conversionQuestions
    .slice(0, half)
    .map((q, idx) =>
      paragraph([
        textNode(`${idx + 1}. `, [{ type: "bold" }]),
        ...inlineFromMaybeMath(q.answer ?? ""),
      ])
    );

  // Build right column paragraphs (16–30)
  const rightColumnContent = conversionQuestions
    .slice(half)
    .map((q, idx) =>
      paragraph([
        textNode(`${idx + half + 1}. `, [{ type: "bold" }]),
        ...inlineFromMaybeMath(q.answer ?? ""),
      ])
    );

  // Push a SINGLE table with ONE row and TWO cells
  doc.content.push({
    type: "table",
    content: [
      {
        type: "tableRow",
        content: [
          {
            type: "tableCell",
            content: leftColumnContent,
          },
          {
            type: "tableCell",
            content: rightColumnContent,
          },
        ],
      },
    ],
  });

  return doc;
}

// 2) column-operations
export function buildColumnOperationsDoc(columnQuestions = []) {
  const doc = { type: "doc", content: [] };

  // --- helper: build one 4x4 column-op table ---
  const buildColumnGroupTable = (problemsSlice, startIndex, showAnswers) => {
    const cellsForRow = (rowType) => {
      const cells = [];

      for (let col = 0; col < 4; col++) {
        const problem = problemsSlice[col];

        if (!problem) {
          // empty cell
          cells.push({
            type: "tableCell",
            content: [{ type: "paragraph" }],
          });
          continue;
        }

        let cellContent = [];

        if (rowType === "number") {
          cellContent = [
            {
              type: "text",
              text: `${startIndex + col + 1}.`,
              marks: [{ type: "bold" }],
            },
          ];
        }

        if (rowType === "firstNum") {
          cellContent = [
            { type: "text", text: String(problem.firstNum ?? "") },
          ];
        }

        if (rowType === "secondNum") {
          cellContent = [
            {
              type: "text",
              text: `${String(problem.operator ?? "+")} ${String(
                problem.secondNum ?? ""
              )}`,
            },
          ];
        }

        if (rowType === "answer") {
          cellContent = showAnswers
            ? [{ type: "text", text: String(problem.answer ?? "") }]
            : [];
        }

        cells.push({
          type: "tableCell",
          content: [
            {
              type: "paragraph",
              // omit content field when empty so it matches your existing behavior
              ...(cellContent.length ? { content: cellContent } : {}),
            },
          ],
        });
      }

      return cells;
    };

    return {
      type: "table",
      attrs: {
        columnMath: true,
      },
      content: [
        { type: "tableRow", content: cellsForRow("number") },
        { type: "tableRow", content: cellsForRow("firstNum") },
        { type: "tableRow", content: cellsForRow("secondNum") },
        { type: "tableRow", content: cellsForRow("answer") },
      ],
    };
  };

  // --- Worksheet: groups of 4 questions ---
  for (let i = 0; i < columnQuestions.length; i += 4) {
    const slice = columnQuestions.slice(i, i + 4);
    doc.content.push(buildColumnGroupTable(slice, i, false));
  }

  for (let i = 0; i < 16; i++) {
    doc.content.push({ type: "paragraph" });
  }

  // --- Answer Key ---
  doc.content.push({
    type: "heading",
    attrs: { level: 2 },
    content: [{ type: "text", text: "Answer Key" }],
  });

  columnQuestions.forEach((problem, idx) => {
    doc.content.push({
      type: "paragraph",
      content: [
        {
          type: "text",
          text: `${idx + 1}. `,
          marks: [{ type: "bold" }],
        },
        {
          type: "text",
          text: String(problem.answer ?? ""),
        },
      ],
    });
  });

  return doc;
}

// 3) exam-template



const isDiagram = (item) => item !== null && typeof item === "object" && item.type === "diagram";

export function buildExamDoc(examQuestions = [], title = 'Maths practice') {
  const content = [
    { type: 'worksheetHeading', content: [{ type: 'text', text: title || 'Maths practice' }] },
    { type: 'paragraph', content: [{ type: 'text', text: 'Name: __________________________     Date: ______________' }] },
    { type: 'horizontalRule' },
  ];

  let questionCounter = 0;
  for (let index = 0; index < examQuestions.length; index++) {
    const q = examQuestions[index];
    if (typeof q === 'string' && /^[#*\s]*answers[*:\s]*$/i.test(q.trim())) {
      const table = createAnswerTable(questionCounter, examQuestions.slice(index + 1));
      const rows = table?.content || [];
      if (!rows.length) rows.push(null);
      rows.forEach((row, i) => content.push({ type: 'answerSection', attrs: { start: i === 0 }, content: [
        ...(i === 0 ? [{ type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Answer key' }] }] : []),
        ...(row ? [{ ...table, content: [row] }] : [{ type: 'paragraph' }]),
      ] }));
      break;
    }
    if (isDiagram(q)) {
      const previous = content[content.length - 1];
      if (previous?.type === 'questionBlock') previous.content.push({ type: 'diagram', attrs: { spec: q.spec } });
      continue;
    }
    questionCounter++;
    const parts = parseContentWithMath(q);
    content.push({ type: 'questionBlock', content: [{
      type: 'paragraph', content: parts.map(part => part.type === 'text'
        ? { type: 'text', text: part.text } : { type: 'mathInline', attrs: part.attrs }),
    }] });
  }

  return {
    type: "doc",
    content,
  };
}

// Helper function to create answer tables with actual answers
// Helper function to create answer tables with actual answers
function createAnswerTable(totalQuestions, answers = []) {
  if (totalQuestions === 0) return null;

  let columns = 1;
  if (totalQuestions === 30) {
    columns = 3;
  } else if (totalQuestions === 20) {
    columns = 2;
  } else if (totalQuestions === 10) {
    columns = 1;
  } else {
    // For other counts, default to appropriate layout
    columns = Math.min(3, Math.ceil(totalQuestions / 10));
  }

  const questionsPerColumn = Math.ceil(totalQuestions / columns);
  const rows = [];

  for (let row = 0; row < questionsPerColumn; row++) {
    const cells = [];
    
    for (let col = 0; col < columns; col++) {
      const questionNum = row + col * questionsPerColumn + 1;
      
      if (questionNum <= totalQuestions) {
        // Get the answer for this question (questionNum - 1 because array is 0-indexed)
        const answer = answers[questionNum - 1] || "";
        
        // Build content with question number and answer (with math support)
        const cellContent = [];
        
        // Add the answer with math notation support
        const answerParts = inlineFromMaybeMath(answer);
        cellContent.push(...answerParts);
        
        cells.push({
          type: "tableCell",
          content: [
            {
              type: "paragraph",
              content: cellContent,
            },
          ],
        });
      } else {
        // Empty cell if we've run out of questions
        cells.push({
          type: "tableCell",
          content: [
            {
              type: "paragraph",
              content: [],
            },
          ],
        });
      }
    }

    rows.push({
      type: "tableRow",
      content: cells,
    });
  }

  return {
    type: "table",
    content: rows,
  };
}


function buildCountingDoc(questions) {
  const content = [];

  // Top Name / Score table
  content.push({
    type: "table",
    content: [
      {
        type: "tableRow",
        content: [
          {
            type: "tableCell",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Name:" }],
              },
            ],
          },
          {
            type: "tableCell",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Score:" }],
              },
            ],
          },
        ],
      },
    ],
  });

  // Spacer
  content.push({ type: "paragraph", content: [] });

  // Title (shown once)
  content.push({
    type: "paragraph",
    content: [
      {
        type: "text",
        text: "Count the pictures",
        marks: [{ type: "bold" }],
      },
    ],
  });

  // Build main table (2 questions per row)
  const rows = [];
  for (let i = 0; i < questions.length; i += 2) {
    const pair = questions.slice(i, i + 2);

    rows.push({
      type: "tableRow",
      content: pair.map((q) => ({
        type: "tableCell",
        attrs: { colwidth: [350] }, // Restrict column width to 200 pixels

        content: [
          // Emojis
          {
            type: "heading",
            attrs: { level: 2 },
            content: [{ type: "text", text: q.images.join(" ") }],
          },
          // Right-aligned answer box
          {
            type: "paragraph",
            attrs: { textAlign: "right" },
            content: [{ type: "answerBox" }],
          },
        ],
      })),
    });
  }

  content.push({ type: "table", content: rows });

  // Build the answers section using the ## Answers marker approach
  const examQuestions = [
    "## Answers",
    ...questions.map((q, i) => `${i + 1}. ${q.answer}`),
  ];

  examQuestions.forEach((q) => {
    const isAnswersHeading = typeof q === "string" && q.trim() === "## Answers";

    // Insert spacing BEFORE the heading
    if (isAnswersHeading) {
      for (let i = 0; i < 18; i++) {
        content.push({ type: "paragraph" });
      }
    }

    content.push({
      type: "paragraph",
      content: [
        {
          type: "text",
          text: isAnswersHeading ? "Answers" : q,
          marks: isAnswersHeading ? [{ type: "bold" }] : undefined,
        },
      ],
    });
  });

  return {
    type: "doc",
    content,
  };
}

function buildPictureOpsDoc(questions) {
  const content = [];

  // Header with Name and Score
  content.push({
    type: "table",
    content: [
      {
        type: "tableRow",
        content: [
          {
            type: "tableCell",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Name:" }],
              },
            ],
          },
          {
            type: "tableCell",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Score:" }],
              },
            ],
          },
        ],
      },
    ],
  });

  content.push({ type: "paragraph", content: [] }); // spacer

  // Helper function to format emojis with spaces and line breaks
  function formatEmojis(emoji, count) {
    const emojiArray = Array(count).fill(emoji);
    const lines = [];

    // Split into chunks of 10
    for (let i = 0; i < emojiArray.length; i += 10) {
      const chunk = emojiArray.slice(i, i + 10);
      lines.push(chunk.join(" ")); // Add space between each emoji
    }

    return lines;
  }

  // Each question in its own card/box
  questions.forEach((q) => {
    // Question heading
    content.push({
      type: "paragraph",
      content: [
        {
          type: "text",
          text: `Question ${q.questionNumber}`,
          marks: [{ type: "bold" }],
        },
      ],
    });

    // Format emojis with spaces and line breaks
    const emoji1Lines = formatEmojis(q.emoji1, q.num1);
    const emoji2Lines = formatEmojis(q.emoji1, q.num2);

    // Table for the equation layout (with no borders and fixed column widths)
    const equationRow = {
      type: "tableRow",
      content: [
        // Left side - first set of emojis (fixed width)
        {
          type: "tableCell",
          attrs: {
            borderColor: "transparent",
            colwidth: [250], // Fixed width in pixels
          },
          content: emoji1Lines.map((line) => ({
            type: "paragraph",
            content: [{ type: "text", text: line }],
          })),
        },
        // Operation symbol (narrow column)
        {
          type: "tableCell",
          attrs: {
            borderColor: "transparent",
            colwidth: [50],
          },
          content: [
            {
              type: "paragraph",
              attrs: { textAlign: "center" },
              content: [
                { type: "text", text: q.operation, marks: [{ type: "bold" }] },
              ],
            },
          ],
        },
        // Right side - second set of emojis (fixed width)
        {
          type: "tableCell",
          attrs: {
            borderColor: "transparent",
            colwidth: [250],
          },
          content: emoji2Lines.map((line) => ({
            type: "paragraph",
            content: [{ type: "text", text: line }],
          })),
        },
        // Equals sign (narrow column)
        {
          type: "tableCell",
          attrs: {
            borderColor: "transparent",
            colwidth: [50],
          },
          content: [
            {
              type: "paragraph",
              attrs: { textAlign: "center" },
              content: [{ type: "text", text: "=", marks: [{ type: "bold" }] }],
            },
          ],
        },
        // Answer box (fixed width)
        {
          type: "tableCell",
          attrs: {
            borderColor: "transparent",
            colwidth: [100],
          },
          content: [
            {
              type: "paragraph",
              attrs: { textAlign: "center" },
              content: [{ type: "answerBox" }],
            },
          ],
        },
      ],
    };

    content.push({
      type: "table",
      attrs: { borderColor: "transparent" },
      content: [equationRow],
    });
  });

  // Spacer before answers
  for (let i = 0; i < 9; i++) {
    content.push({ type: "paragraph" });
  }

  // Answers section with heading
  content.push({
    type: "paragraph",
    attrs: { textAlign: "center" },
    content: [
      {
        type: "text",
        text: "Answer Key",
        marks: [{ type: "bold" }],
      },
    ],
  });

  content.push({ type: "paragraph", content: [] });

  // Answers in a cleaner format
  questions.forEach((q) => {
    content.push({
      type: "paragraph",
      content: [
        {
          type: "text",
          text: `${q.questionNumber}. `,
          marks: [{ type: "bold" }],
        },
        {
          type: "text",
          text: `${q.num1} ${q.operation} ${q.num2} = ${q.answer}`,
        },
      ],
    });
  });

  // Footer
  content.push({ type: "paragraph", content: [] });
  content.push({ type: "paragraph", content: [] });

  return {
    type: "doc",
    content,
  };
}

// Dispatcher
export function buildDocFromTemplate(template_id, questions, title) {
  const safeQuestions = Array.isArray(questions) ? questions : [];

  switch (template_id) {
    case "conversion-template":
      return buildConversionDoc(safeQuestions);

    case "column-template":
      return buildColumnOperationsDoc(safeQuestions);

    case "exam-style":
      return buildExamDoc(safeQuestions, title);

    case "counting-template":
      return buildCountingDoc(safeQuestions);

    case "picture-ops-template":
      return buildPictureOpsDoc(safeQuestions);

    default:
      return { type: "doc", content: [] };
  }
}

// Explicit upgrade for existing exam worksheets. Preserve edited text/math and
// diagrams; only replace the old empty-paragraph spacing and answer heading.
export function upgradeExamLayout(doc) {
  const flat = [];
  const collect = node => {
    if (['doc', 'page', 'body'].includes(node.type)) (node.content || []).forEach(collect);
    else if (!['header-footer'].includes(node.type)) flat.push(node);
  };
  collect(doc);
  if (flat.some(node => node.type === 'questionBlock')) return doc;
  const output = [];
  let answers = false;
  for (const node of flat) {
    const text = (node.content || []).filter(n => n.type === 'text').map(n => n.text).join('');
    if (node.type === 'paragraph' && !node.content?.length) continue;
    if (node.type === 'paragraph' && /^[#*\s]*answers[*:\s]*$/i.test(text.trim())) {
      answers = true;
      continue;
    }
    if (answers) {
      const start = !output.some(n => n.type === 'answerSection');
      if (node.type === 'table') {
        node.content.forEach((row, i) => output.push({ type: 'answerSection', attrs: { start: start && i === 0 }, content: [
          ...(start && i === 0 ? [{ type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Answer key' }] }] : []),
          { ...node, content: [row] },
        ] }));
      } else output.push({ type: 'answerSection', attrs: { start }, content: [node] });
    } else if (node.type === 'paragraph' && /^\s*\d+[.)]/.test(text)) {
      output.push({ type: 'questionBlock', content: [node] });
    } else if (output[output.length - 1]?.type === 'questionBlock' && ['paragraph', 'diagram'].includes(node.type)) {
      output[output.length - 1].content.push(node);
    } else output.push(node);
  }
  return { type: 'doc', content: output };
}
